import React, { useState } from 'react';
import ImageGallery from 'react-image-gallery';
import { X, Download, ZoomIn } from 'lucide-react';
import 'react-image-gallery/styles/css/image-gallery.css';

const ModernImageGallery = ({ 
    images = [], 
    showThumbnails = true,
    showPlayButton = false,
    showFullscreenButton = true,
    showNav = true,
    autoPlay = false,
    className = ''
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    if (!images || images.length === 0) {
        return (
            <div className={`flex items-center justify-center p-8 bg-gray-100 rounded-lg ${className}`}>
                <div className="text-center">
                    <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <p className="text-gray-500">No images available</p>
                </div>
            </div>
        );
    }

    // Transform images to react-image-gallery format
    const galleryImages = images.map((image, index) => {
        if (typeof image === 'string') {
            return {
                original: image,
                thumbnail: image,
                originalAlt: `Image ${index + 1}`,
                thumbnailAlt: `Thumbnail ${index + 1}`
            };
        }
        
        return {
            original: image.original || image.large || image.medium || image.thumb || image,
            thumbnail: image.thumbnail || image.thumb || image.medium || image.original || image,
            originalAlt: image.alt || `Image ${index + 1}`,
            thumbnailAlt: image.thumbnailAlt || `Thumbnail ${index + 1}`,
            description: image.description || '',
            originalTitle: image.title || ''
        };
    });

    const customControls = () => (
        <div className="absolute top-4 right-4 z-10 flex space-x-2">
            <button
                onClick={() => setIsFullscreen(true)}
                className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all"
                title="Fullscreen"
            >
                <ZoomIn size={16} />
            </button>
            <button
                onClick={() => {
                    const currentImage = galleryImages[0]; // Get current image
                    if (currentImage) {
                        const link = document.createElement('a');
                        link.href = currentImage.original;
                        link.download = `image-${Date.now()}.jpg`;
                        link.click();
                    }
                }}
                className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all"
                title="Download"
            >
                <Download size={16} />
            </button>
        </div>
    );

    return (
        <div className={`modern-image-gallery ${className}`}>
            <div className="relative">
                <ImageGallery
                    items={galleryImages}
                    showThumbnails={showThumbnails}
                    showPlayButton={showPlayButton}
                    showFullscreenButton={showFullscreenButton}
                    showNav={showNav}
                    autoPlay={autoPlay}
                    slideInterval={3000}
                    slideDuration={450}
                    renderCustomControls={customControls}
                    thumbnailPosition="bottom"
                    useBrowserFullscreen={false}
                    onScreenChange={(fullscreen) => setIsFullscreen(fullscreen)}
                />
            </div>

            {/* Custom Fullscreen Modal */}
            {isFullscreen && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
                    <button
                        onClick={() => setIsFullscreen(false)}
                        className="absolute top-4 right-4 z-10 p-2 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30 transition-all"
                    >
                        <X size={24} />
                    </button>
                    
                    <div className="w-full h-full flex items-center justify-center p-4">
                        <ImageGallery
                            items={galleryImages}
                            showThumbnails={false}
                            showPlayButton={false}
                            showFullscreenButton={false}
                            showNav={true}
                            autoPlay={false}
                            slideDuration={300}
                            useBrowserFullscreen={false}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModernImageGallery;
